import { Idea, IdeaMetricTag } from '@frontend/data-access';
import { IdeaChannelDetail } from '@frontend/feature-idea-channel';
import { UserAvatar } from '@frontend/shared/ui';
import { Avatar, Modal } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useState } from 'react';
import IdeaChannelEvaluationResultTag from '../idea-channel-evaluation-result-tag/idea-channel-evaluation-result-tag';
import useTranslation from 'next-translate/useTranslation';

// Initialize dayjs plugins
dayjs.extend(relativeTime);

/* eslint-disable-next-line */
export interface IdeaChannelCardProps {
  data: Idea;
}

export const MetricTag = ({ tag }: { tag: IdeaMetricTag }) => {
  const { t } = useTranslation('common');

  let text = t('no_metrics');
  let color = 'bg-red-100 text-red-600';

  switch (tag) {
    case 'NONE':
      text = t('no_metrics');
      color = 'bg-gray-300';
      break;
    case 'NO_TARGET':
      text = t('no_target_metrics');
      color = 'bg-red-100 text-red-600';
      break;
    case 'TARGET':
      text = t('target_metrics');
      color = 'bg-blue-100 text-blue-600';
      break;
    case 'RESULT':
      text = t('actual_result');
      color = 'bg-green-100 text-green-600';
      break;
  }
  return (
    <div className="text-left">
      <div
        className={`text-xs ${color} px-2 py-1 rounded-full w-min whitespace-nowrap`}
      >
        {text}
      </div>
    </div>
  );
};

export function IdeaChannelCard({ data }: IdeaChannelCardProps) {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const handleOpenModal = () => {
    setIsOpenModal(!isOpenModal);
  };

  const IdeaChannelTags = () => {
    if (data.channelTag) {
      return (
        <div className="flex gap-2 flex-wrap">
          {data.channelTag?.slice(0, 3).map((item, index) => (
            <div
              key={index}
              className="px-3 py-1 bg-gray-300 rounded-full text-gray-800"
            >
              {item.name}
            </div>
          ))}
          {data.channelTag?.length > 3 && (
            <div className="px-3 py-1 bg-gray-300 rounded-full text-gray-800">
              +{data.channelTag?.length - 3}
            </div>
          )}
        </div>
      );
    } else {
      return null;
    }
  };

  return (
    <>
      <div
        className="space-y-3"
        data-test="idea-card"
        onClick={handleOpenModal}
      >
        <MetricTag tag={data.metricTag} />
        <div className="flex items-center gap-2">
          <UserAvatar size={24} user={data.createdBy} isClickable={false} />
          <span className="text-sm text-gray-800 font-normal truncate">
            {data.createdBy.fullName}
          </span>
        </div>
        <h1 className="text-sm text-gray-900 font-normal text-ellipsis overflow-hidden">
          {data.name}
        </h1>
        <IdeaChannelTags />
        <IdeaChannelEvaluationResultTag results={data.channelCriteriaResults} />
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-gray-700 font-normal">
            {data.age && dayjs(data.age).fromNow(true)}
          </div>
          <div>
            <Avatar.Group
              size="small"
              max={{
                count: 2,
                style: { background: '#bab8b8' },
              }}
            >
              {data.ideaChannelMembers?.slice(0, 2)?.map((item, index) => (
                <UserAvatar
                  key={index}
                  size={24}
                  user={item.user}
                  isClickable={false}
                />
              ))}
            </Avatar.Group>
          </div>
        </div>
      </div>
      <Modal
        centered
        title={null}
        open={isOpenModal}
        onCancel={handleOpenModal}
        width={1024}
        footer={null}
        destroyOnClose
      >
        <IdeaChannelDetail id={data.id} />
      </Modal>
    </>
  );
}

export default IdeaChannelCard;
